import { useRef, useState, type ChangeEvent } from "react";
import { PlatformApiError, type PlatformApiClient } from "../../api";
import "./acquisition-preview.css";
import { CampaignManagementView } from "./CampaignManagementView";
import { REVIEWBOOST_V4_UI_ENABLED } from "../../review-boost-v4-ui";

interface ImportSummary {
  total: number;
  eligibleCandidates: number;
  blocked: number;
  suppressed: number;
  duplicate: number;
}
interface ImportPreview {
  dryRun: true;
  persistAuthorized: false;
  sendAuthorized: false;
  globalSuppressionStatus: string;
  customerIdentityStatus: string;
  summary: ImportSummary;
}
type AcquisitionTask = "source" | "campaigns" | "preview" | "requirements";

type PreviewState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; summary: ImportSummary }
  | { kind: "disabled" }
  | { kind: "forbidden" }
  | { kind: "error"; message: string };

const MAX_BYTES = 512 * 1024;
const MAX_NAME = 100;

/** Preparation only: no browser connector credentials or inferred source status. */
export function AcquisitionSourceGuide({ onPreview, onCampaigns }: {
  onPreview: () => void; onCampaigns: () => void;
}) {
  return <section className="rbv4-source-guide" aria-labelledby="rbv4-source-title">
    <p className="rep-acquisition-kicker">1 · Origen de clientes</p>
    <h2 id="rbv4-source-title">Prepará tu fuente CRM/API</h2>
    <p>Usá los eventos de tu sistema para preparar solicitudes de reseñas con permisos verificables.</p>
    <p><strong>Conexión desde esta pantalla: NO DISPONIBLE.</strong> No tenemos evidencia de una fuente configurada. La preparación no conecta cuentas ni habilita envíos.</p>
    <details className="rbv4-source-onboarding">
      <summary>Preparar fuente CRM/API</summary>
      <ol>
        <li><strong>Identificá la fuente.</strong> Definí con el responsable de tu CRM el negocio, la ubicación y el evento que confirma una experiencia finalizada. Cada evento necesita un identificador estable para evitar duplicados.</li>
        <li><strong>Verificá permisos por canal.</strong> Para email y WhatsApp, conservá por separado la autorización de la persona, su fecha, origen y finalidad. Tener un contacto o una compra no implica consentimiento.</li>
        <li><strong>Prepará las bajas.</strong> Respetá STOP en WhatsApp y opt-out en email. Las supresiones deben comprobarse nuevamente antes de cada envío, incluso si la persona estaba autorizada al ingresar.</li>
        <li><strong>Solicitá la configuración al responsable de la integración.</strong> Las credenciales se provisionan y verifican en el servidor. No pegues tokens ni claves en el navegador. Google Sheets no tiene conexión OAuth disponible aquí.</li>
        <li><strong>Validá antes de activar.</strong> Confirmá identidad, permisos de acceso, campaña, canal, demora y límites con evidencia del backend. Un evento recibido o un proveedor que acepta un mensaje no prueba entrega.</li>
      </ol>
      <p>No se guardó una configuración ni un consentimiento. La conexión y la autorización de envíos siguen pendientes de verificación.</p>
    </details>
    <div className="rbv4-source-alternatives">
      <h3>Otras opciones</h3>
      <button type="button" onClick={onPreview}>Planilla o CSV · Verificar sin importar</button>
      <p>Alternativa secundaria: dry-run de formato, duplicados y consentimiento declarado. No verifica identidad ni supresiones globales; no guarda contactos ni envía mensajes.</p>
      <button type="button" onClick={onCampaigns}>Preparar con clientes existentes →</button>
    </div>
  </section>;
}

export function AcquisitionPreviewView({
  slug, api, onExpired,
}: {
  slug: string;
  api: PlatformApiClient;
  onExpired: () => void;
}) {
  const [task, setTask] = useState<AcquisitionTask>(REVIEWBOOST_V4_UI_ENABLED ? "source" : "campaigns");
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [state, setState] = useState<PreviewState>({ kind: "idle" });

  function selectFile(event: ChangeEvent<HTMLInputElement>) {
    const next = event.target.files?.[0] ?? null;
    setFile(next);
    setState({ kind: "idle" });
  }

  async function inspect() {
    if (!file) return;
    if (file.size === 0 || file.size > MAX_BYTES) {
      setState({ kind: "error", message: "El archivo debe ser un CSV de entre 1 byte y 512 KB." });
      return;
    }
    if (!/\.csv$/i.test(file.name) && file.type !== "text/csv") {
      setState({ kind: "error", message: "Seleccioná un archivo CSV." });
      return;
    }
    setState({ kind: "loading" });
    try {
      const csv = await file.text();
      // Native Reputation preview is authenticated and explicitly OFF by default.
      // The file never enters browser storage, logs or an outbound provider.
      const result = await api.reputation<ImportPreview>(slug, "/acquisition/preview", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ csv }),
      });
      if (result.dryRun !== true || result.sendAuthorized !== false ||
          result.persistAuthorized !== false || !result.summary ||
          result.globalSuppressionStatus !== "not_checked" ||
          result.customerIdentityStatus !== "not_matched") {
        setState({ kind: "error", message: "La respuesta no cumple las garantías de vista previa." });
        return;
      }
      const { summary } = result;
      if (![summary.total, summary.eligibleCandidates, summary.blocked,
        summary.suppressed, summary.duplicate].every((count) => Number.isSafeInteger(count) && count >= 0 && count <= summary.total)) {
        setState({ kind: "error", message: "No se pudo verificar el resumen recibido." });
        return;
      }
      setState({ kind: "ready", summary });
    } catch (cause) {
      if (cause instanceof PlatformApiError) {
        if (cause.status === 401) { onExpired(); return; }
        if (cause.status === 404) { setState({ kind: "disabled" }); return; }
        if (cause.status === 403) { setState({ kind: "forbidden" }); return; }
        if (cause.status === 413) {
          setState({ kind: "error", message: "El CSV supera el tamaño admitido por el servidor." });
          return;
        }
      }
      setState({ kind: "error", message: "No se pudo analizar el CSV. Revisá el formato y volvé a intentarlo." });
    }
  }

  const tasks: AcquisitionTask[] = REVIEWBOOST_V4_UI_ENABLED ? ["source", "preview", "campaigns", "requirements"] : ["campaigns", "preview", "requirements"];
  const taskCopy: Record<AcquisitionTask, [string, string]> = {
    source: ["Origen de clientes", "Elegir tu fuente"],
    preview: ["Verificar clientes", "Analizar CSV sin importar ni enviar"],
    campaigns: ["Campañas", "Preparar, aprobar y programar borradores"],
    requirements: ["Antes de enviar", "Consentimientos, permisos y limitaciones"],
  };
  const taskNavigation = <div className="rep-acquisition-task-switch" role="group" aria-label="Tareas de Captación">
    {tasks.map((item, index) => <button key={item} type="button" aria-pressed={task === item} onClick={() => setTask(item)}>
      {REVIEWBOOST_V4_UI_ENABLED && <span className="rbv4-step-number" aria-hidden="true">{index + 1}</span>}
      <strong>{taskCopy[item][0]}</strong><span>{taskCopy[item][1]}</span>
    </button>)}
  </div>;

  return (
    <section className="rep-acquisition" aria-labelledby="rep-acquisition-title">
      <header className="rep-acquisition-heading">
        <div>
          <p className="rep-acquisition-kicker">Review Boost · Captación</p>
          <h1 id="rep-acquisition-title">Conseguir reseñas</h1>
          <p>Prepará campañas, verificá listas de clientes y conocé los requisitos antes de solicitar reseñas.</p>
        </div>
        <span className="rep-acquisition-mode">Preparación · Sin envíos</span>
      </header>

      {REVIEWBOOST_V4_UI_ENABLED && taskNavigation}
      {REVIEWBOOST_V4_UI_ENABLED && task === "source" &&
        <AcquisitionSourceGuide onPreview={() => setTask("preview")} onCampaigns={() => setTask("campaigns")} />}

      {!REVIEWBOOST_V4_UI_ENABLED && taskNavigation}

      <p className="rbv3-mobile-scroll-hint">Deslizá para ver las otras opciones →</p>

      {/* Preserve task trees so navigating does not discard a CSV preview or campaign draft. */}
      <div className="rep-acquisition-task-content" hidden={task !== "preview"}
        role="region" aria-label="Verificación preliminar de clientes">
      <div className="rep-acquisition-panel">
        <h2>Verificar archivo de clientes</h2>
        <p>Subí un archivo CSV para revisar estructura, duplicados y consentimiento declarado. No se guardará ninguna lista ni se enviarán invitaciones en esta etapa.</p>
        <label htmlFor="rep-acquisition-file">Archivo CSV</label>
        <p id="rep-acquisition-file-help">Usá un archivo .csv de hasta 512 KB. Verificar no guarda clientes ni habilita envíos.</p>
        <input
          ref={fileInput}
          id="rep-acquisition-file"
          aria-describedby="rep-acquisition-file-help"
          aria-invalid={state.kind === "error" || undefined}
          type="file"
          accept=".csv,text/csv"
          disabled={state.kind === "loading"}
          onChange={selectFile}
        />
        {file && <p className="rep-acquisition-file">
          Seleccionado: {file.name.slice(0, MAX_NAME)} · {(file.size / 1024).toFixed(1)} KB
        </p>}
        <div className="rep-acquisition-actions">
          <button type="button" className="primary-button"
            disabled={!file || state.kind === "loading"} onClick={() => { void inspect(); }}>
            {state.kind === "loading" ? "Analizando…" : "Verificar archivo"}
          </button>
          <button type="button" disabled={state.kind === "loading"}
            onClick={() => { setFile(null); if (fileInput.current) fileInput.current.value = ""; setState({ kind: "idle" }); }}>
            Limpiar
          </button>
        </div>

        <div className="rep-acquisition-result" aria-live="polite" role="status">
          {state.kind === "idle" && <p>Seleccioná un archivo para comenzar.</p>}
          {state.kind === "loading" && <p>Comprobando el CSV sin guardar destinatarios…</p>}
          {state.kind === "disabled" &&
            <p>La vista previa de Captación todavía no está habilitada para esta organización. No se importó ni se envió nada.</p>}
          {state.kind === "forbidden" &&
            <p>Tu usuario no tiene permiso para analizar listas de clientes de este negocio.</p>}
          {state.kind === "error" && <p>{state.message}</p>}
          {state.kind === "ready" && <>
            <h3>Resumen de la vista previa</h3>
            <div className="rep-acquisition-metrics">
              <div><strong>{state.summary.total}</strong><span>Filas</span></div>
              <div><strong>{state.summary.eligibleCandidates}</strong><span>Candidatos preliminares</span></div>
              <div><strong>{state.summary.blocked}</strong><span>Bloqueados</span></div>
              <div><strong>{state.summary.duplicate}</strong><span>Duplicados</span></div>
              <div><strong>{state.summary.suppressed}</strong><span>Excluidos del CSV</span></div>
            </div>
          </>}
        </div>
        {REVIEWBOOST_V4_UI_ENABLED && <div className="rbv4-grow-next">
          <button type="button" onClick={() => setTask("source")}>← Volver a la fuente</button>
          {state.kind === "ready" && <><p>La verificación es preliminar. No se importó esta lista. Las campañas usan clientes ya disponibles en el sistema.</p><button type="button" onClick={() => setTask("campaigns")}>Ver preparación de campañas →</button></>}
        </div>}
      </div>
      </div>
      <div className="rep-acquisition-task-content" hidden={task !== "campaigns"}
        role="region" aria-label="Campañas en preparación">
        <CampaignManagementView slug={slug} api={api} onExpired={onExpired} />
      </div>
      <div className="rep-acquisition-task-content" hidden={task !== "requirements"}
        role="region" aria-label="Requisitos antes de contactar">
      <div className="rep-acquisition-disclaimer">
        <h2>Antes de contactar a cualquier cliente</h2>
        <p>La vista previa todavía no verifica si cada persona ya figura como cliente, su compra, su permiso para recibir mensajes ni todas las solicitudes de baja. Un candidato preliminar no está autorizado para recibir mensajes.</p>
        <p><strong>Campañas y envíos reales deshabilitados.</strong> No se generarán mensajes por email ni WhatsApp desde esta pantalla.</p>
      </div>
      </div>
    </section>
  );
}
